/**
 * Privacy Policy — full document.
 * Editorial-scientific layout: hero plate, sticky TOC, numbered sections.
 * Substantive B2B HR-SaaS privacy content (controller/processor split, GDPR/CCPA
 * rights, retention schedules, sub-processor list, international transfers).
 */

const SECTIONS = [
  { id: 'roles',            label: 'Our role: controller and processor' },
  { id: 'data-we-collect',  label: 'Categories of personal data we collect' },
  { id: 'how-we-use',       label: 'How we use personal data' },
  { id: 'lawful-basis',     label: 'Lawful basis for processing (GDPR)' },
  { id: 'sub-processors',   label: 'Sub-processors' },
  { id: 'international',    label: 'International data transfers' },
  { id: 'retention',        label: 'Retention schedules' },
  { id: 'security',         label: 'Security & encryption' },
  { id: 'rights',           label: 'Your rights as a data subject' },
  { id: 'ccpa',             label: 'California (CCPA / CPRA) disclosures' },
  { id: 'cookies',          label: 'Cookies & similar technologies' },
  { id: 'children',         label: "Children's data" },
  { id: 'changes',          label: 'Changes to this policy' },
  { id: 'contact-dpo',      label: 'Contact our DPO' },
];

export default function Privacy() {
  return (
    <article>
      <section className="cz-legal-hero">
        <div className="cz-wrap">
          <div>
            <span className="cz-mono-label">Document · Privacy Policy</span>
            <h1>How we handle <em>your data.</em></h1>
          </div>
          <dl className="cz-legal-hero-meta">
            <dt>Effective</dt><dd>2026-04-01</dd>
            <dt>Version</dt><dd>v 3.2</dd>
            <dt>Supersedes</dt><dd>2025-09-15 (v 3.1)</dd>
            <dt>Reading time</dt><dd>≈ 14 minutes</dd>
          </dl>
        </div>
      </section>

      <section className="cz-legal-body">
        <div className="cz-wrap">
          <div className="cz-legal-grid">
            <nav className="cz-legal-toc" aria-label="Contents">
              <span className="cz-legal-toc-head">§ Contents</span>
              <ol>
                {SECTIONS.map(s => (
                  <li key={s.id}><a href={`#${s.id}`}>{s.label}</a></li>
                ))}
              </ol>
            </nav>

            <div className="cz-legal-article">
              <section>
                <p>
                  CalibZa, Inc. (&ldquo;<strong>CalibZa</strong>,&rdquo; &ldquo;we,&rdquo; &ldquo;us&rdquo;) provides
                  performance-calibration software to organisations. This Privacy
                  Policy explains what personal data we collect, why we collect it,
                  how we use it, and the rights you have over it.
                </p>
                <p>
                  This policy applies to data processed through our website at
                  <code>calibza.com</code>, our customer dashboards at
                  <code>*.calibza.com</code>, and our APIs at <code>api.calibza.com</code>.
                  It does not cover data your employer separately processes inside
                  CalibZa &mdash; in those cases your employer is the controller and you should
                  consult their internal privacy notice.
                </p>
                <div className="cz-legal-callout">
                  <p>
                    The short version: <em>we don&rsquo;t sell personal data, ever.</em>
                    Your employer&rsquo;s data is theirs, encrypted, and exportable on demand.
                    We process it only to provide the service.
                  </p>
                </div>
              </section>

              <section id="roles">
                <h2><span className="num">01</span>Our role: controller and processor</h2>
                <p>
                  Privacy law treats us in two distinct ways depending on whose data we
                  are handling:
                </p>
                <ul>
                  <li>
                    <strong>As a controller</strong> &mdash; for data we collect from website
                    visitors, prospects, applicants, and the administrators who sign up
                    for a CalibZa account. We decide why and how this data is used.
                  </li>
                  <li>
                    <strong>As a processor</strong> &mdash; for data your employer uploads
                    into CalibZa about you (employee profiles, performance reviews,
                    feedback, calibration records, payroll). Your employer is the
                    controller; we process this data only on their documented instructions
                    under our standard Data Processing Addendum.
                  </li>
                </ul>
                <p>
                  Sections 02&ndash;04 below cover both roles. Sections 09&ndash;11 explain how
                  to exercise your rights against the appropriate party.
                </p>
              </section>

              <section id="data-we-collect">
                <h2><span className="num">02</span>Categories of personal data we collect</h2>

                <h3>Account &amp; identity data</h3>
                <p>
                  Name, work email, role title, company, password hash (Argon2id),
                  profile photo (optional), language preference, timezone.
                </p>

                <h3>Authentication &amp; session data</h3>
                <p>
                  IP address, user agent, login timestamps, session identifiers (signed,
                  HttpOnly cookies), 2FA enrolment status, SSO assertion attributes
                  (sub, email, groups). MFA codes are never stored.
                </p>

                <h3>Performance &amp; HR data (controller = your employer)</h3>
                <p>
                  Job title, department, manager, reporting line, hire date, performance
                  scores, calibration band, peer/manager/self/360 feedback, goals,
                  PIPs, IDPs, succession candidacy, compensation history, payroll
                  withholding inputs, time-off, banking details (encrypted at rest with
                  customer-held KMS where available).
                </p>

                <h3>Usage &amp; product analytics</h3>
                <p>
                  Anonymised event telemetry: pages viewed, features used, error counts,
                  request latencies, browser type. We use first-party tooling
                  (self-hosted PostHog) and do not load third-party trackers.
                </p>

                <h3>Communications</h3>
                <p>
                  Support tickets, in-app messages, feedback survey responses, transactional
                  email metadata.
                </p>
              </section>

              <section id="how-we-use">
                <h2><span className="num">03</span>How we use personal data</h2>
                <ul>
                  <li><strong>To deliver the service</strong> &mdash; authentication, calibration cycles, score generation, distribution analytics, payroll runs, integrations, exports.</li>
                  <li><strong>To improve the product</strong> &mdash; aggregated, de-identified usage metrics inform what we build next. Individual records are never used for cross-customer analysis.</li>
                  <li><strong>To provide support</strong> &mdash; agents access tenant data only with case-bound, audit-logged, time-limited grants approved by your tenant administrator.</li>
                  <li><strong>To meet legal obligations</strong> &mdash; tax reporting on payroll runs, lawful disclosure under court order or warrant, breach notification under GDPR Art. 33 and applicable US state laws.</li>
                  <li><strong>To secure the platform</strong> &mdash; rate-limiting, anomaly detection, incident response, audit log retention.</li>
                  <li><strong>To bill</strong> &mdash; we share invoice metadata (company name, seat counts, amounts) with our payment processor.</li>
                </ul>
                <p>
                  We do <strong>not</strong> use your data to train any machine-learning model
                  whose weights are shared across customers. Tenant-scoped models
                  (e.g. anti-gaming detection) are trained only on the tenant&rsquo;s own data.
                </p>
              </section>

              <section id="lawful-basis">
                <h2><span className="num">04</span>Lawful basis for processing (GDPR)</h2>
                <table className="cz-legal-table">
                  <thead>
                    <tr><th>Purpose</th><th>Lawful basis</th></tr>
                  </thead>
                  <tbody>
                    <tr><td>Operating the platform under our contract with your employer</td><td>Art. 6(1)(b) — performance of a contract</td></tr>
                    <tr><td>Authentication, security, fraud prevention</td><td>Art. 6(1)(f) — legitimate interests</td></tr>
                    <tr><td>Tax, payroll, statutory reporting</td><td>Art. 6(1)(c) — legal obligation</td></tr>
                    <tr><td>Aggregated benchmarks (de-identified)</td><td>Art. 6(1)(f) — legitimate interests; opt-out at tenant level</td></tr>
                    <tr><td>Marketing emails to administrators</td><td>Art. 6(1)(a) — consent (with one-click unsubscribe)</td></tr>
                    <tr><td>Special-category data (e.g. health-related accommodations)</td><td>Art. 9(2)(b) — employment-law obligations of the employer</td></tr>
                  </tbody>
                </table>
              </section>

              <section id="sub-processors">
                <h2><span className="num">05</span>Sub-processors</h2>
                <p>
                  We use a small, vetted set of sub-processors. The list below is current
                  as of the effective date; any additions trigger a 30-day advance
                  notification on this page and via email to tenant administrators.
                </p>
                <table className="cz-legal-table">
                  <thead>
                    <tr><th>Provider</th><th>Purpose</th><th>Region</th></tr>
                  </thead>
                  <tbody>
                    <tr><td>Amazon Web Services</td><td>Application hosting, S3, RDS, KMS</td><td>us-east-1, eu-west-1, ap-south-1</td></tr>
                    <tr><td>Cloudflare</td><td>Edge CDN, DDoS protection, WAF</td><td>Global edge</td></tr>
                    <tr><td>Postmark</td><td>Transactional email delivery</td><td>US</td></tr>
                    <tr><td>Stripe</td><td>Subscription billing, invoice payments</td><td>US, EU</td></tr>
                    <tr><td>Anthropic</td><td>AI feature inference (no training on customer data)</td><td>US</td></tr>
                    <tr><td>Sentry (self-hosted)</td><td>Error monitoring</td><td>us-east-1</td></tr>
                  </tbody>
                </table>
              </section>

              <section id="international">
                <h2><span className="num">06</span>International data transfers</h2>
                <p>
                  Tenants choose their primary data residency at sign-up: <strong>US-East</strong>,
                  <strong> EU-West</strong>, or <strong>India-South</strong>. Production data does not leave the
                  selected region except for the limited transfers below, all of which
                  rely on Standard Contractual Clauses (2021/914) and Transfer Impact
                  Assessments updated annually:
                </p>
                <ul>
                  <li>Encrypted, ephemeral support sessions to engineers in the US, UK, and India.</li>
                  <li>Anonymised diagnostic telemetry to Sentry (US-East).</li>
                  <li>AI feature inference to Anthropic (US) — opt-out available at tenant level.</li>
                </ul>
                <p>
                  EU-resident administrators may request a Transfer Impact Assessment summary
                  by emailing our DPO (see &sect;14).
                </p>
              </section>

              <section id="retention">
                <h2><span className="num">07</span>Retention schedules</h2>
                <table className="cz-legal-table">
                  <thead>
                    <tr><th>Data type</th><th>Retention</th></tr>
                  </thead>
                  <tbody>
                    <tr><td>Live tenant data (employees, reviews, scores)</td><td>For the life of the contract; deleted within 30 days of termination unless legal hold</td></tr>
                    <tr><td>Audit logs (immutable, append-only)</td><td>7 years (regulatory)</td></tr>
                    <tr><td>Payroll records</td><td>7 years (IRS / HMRC alignment)</td></tr>
                    <tr><td>Backups</td><td>35-day rolling window, then cryptographically erased</td></tr>
                    <tr><td>Anonymised benchmark snapshots</td><td>Indefinite (cannot be re-identified)</td></tr>
                    <tr><td>Marketing leads / website forms</td><td>24 months from last interaction</td></tr>
                    <tr><td>Support tickets</td><td>3 years; redacted of free-text personal data after 1 year</td></tr>
                  </tbody>
                </table>
              </section>

              <section id="security">
                <h2><span className="num">08</span>Security &amp; encryption</h2>
                <ul>
                  <li><strong>In transit:</strong> TLS 1.3, HSTS preload, OCSP stapling, ECDSA P-384 certificates.</li>
                  <li><strong>At rest:</strong> AES-256-GCM with envelope encryption (DEK per tenant, KEK in AWS KMS, optional customer-managed keys).</li>
                  <li><strong>Application:</strong> argon2id password hashing, signed session cookies, CSRF double-submit, CSP with strict-dynamic, SRI on all third-party scripts (we ship none).</li>
                  <li><strong>Network:</strong> VPC-isolated, private subnets only, no public DB ingress, AWS PrivateLink for sub-processors that support it.</li>
                  <li><strong>Operational:</strong> SOC 2 Type II (annual), ISO 27001 (in progress, target Q3 2026), quarterly third-party penetration tests, internal red-team exercises.</li>
                  <li><strong>Incident response:</strong> 24/7 oncall, 72-hour breach notification under GDPR Art. 33 and applicable US state laws, transparent post-mortems for any incident affecting customer data.</li>
                </ul>
              </section>

              <section id="rights">
                <h2><span className="num">09</span>Your rights as a data subject</h2>
                <p>
                  Where we are the controller (e.g. you signed up for a free trial or used
                  our website), you may exercise the following rights against us directly
                  by emailing <a href="mailto:privacy@calibza.com">privacy@calibza.com</a>:
                </p>
                <ul>
                  <li><strong>Access</strong> — receive a copy of your personal data within 30 days.</li>
                  <li><strong>Rectification</strong> — correct inaccurate data.</li>
                  <li><strong>Erasure</strong> — delete data we hold about you, subject to legal retention.</li>
                  <li><strong>Restriction</strong> — limit our processing while a dispute is resolved.</li>
                  <li><strong>Portability</strong> — receive structured, machine-readable export (JSON or CSV).</li>
                  <li><strong>Objection</strong> — to processing based on legitimate interests or for direct marketing.</li>
                  <li><strong>Withdraw consent</strong> — at any time where consent is the lawful basis.</li>
                  <li><strong>Lodge a complaint</strong> — with your supervisory authority (ICO, CNIL, AEPD, etc.).</li>
                </ul>
                <p>
                  Where we are the processor (your employer&rsquo;s tenant), please direct
                  the request to your employer&rsquo;s privacy contact. We will support them
                  in fulfilling your request within the contractual response window.
                </p>
              </section>

              <section id="ccpa">
                <h2><span className="num">10</span>California (CCPA / CPRA) disclosures</h2>
                <p>
                  In the previous 12 months we have collected the categories of personal
                  information identified in &sect;02. We have <strong>not sold</strong> personal
                  information and we do not <strong>share</strong> it for cross-context
                  behavioral advertising. Sensitive personal information (e.g. SSN for
                  payroll) is processed only for the purposes described in &sect;03.
                </p>
                <p>
                  California residents may exercise the rights to know, delete, correct,
                  and limit use of sensitive personal information by emailing
                  <a href="mailto:privacy@calibza.com">privacy@calibza.com</a> or calling
                  +1 (415) 555-0142. We do not discriminate against rights-exercising users.
                </p>
              </section>

              <section id="cookies">
                <h2><span className="num">11</span>Cookies &amp; similar technologies</h2>
                <p>
                  We use a small, strictly necessary cookie set:
                </p>
                <ul>
                  <li><code>cz_session</code> — signed HttpOnly session token (24 h).</li>
                  <li><code>cz_csrf</code> — CSRF double-submit token (per session).</li>
                  <li><code>cz_consent</code> — your cookie banner choice (12 months).</li>
                  <li><code>cz_locale</code> — language preference (12 months).</li>
                </ul>
                <p>
                  We do not load Google Analytics, Facebook Pixel, or any third-party
                  tracking. Product analytics are first-party (self-hosted PostHog) and
                  cookie-less.
                </p>
              </section>

              <section id="children">
                <h2><span className="num">12</span>Children&rsquo;s data</h2>
                <p>
                  CalibZa is a B2B service. We do not knowingly collect personal data from
                  anyone under 16. If you believe a child has provided us data, contact
                  <a href="mailto:privacy@calibza.com">privacy@calibza.com</a> and we will delete it.
                </p>
              </section>

              <section id="changes">
                <h2><span className="num">13</span>Changes to this policy</h2>
                <p>
                  Material changes are announced 30 days in advance via email to tenant
                  administrators and a banner on the application. Non-material changes
                  (typo fixes, link updates) are reflected in the version history at
                  the top of this page.
                </p>
              </section>

              <section id="contact-dpo">
                <h2><span className="num">14</span>Contact our DPO</h2>
                <p>
                  <strong>Data Protection Officer:</strong> Jordan Pell, JD/CIPP/E.<br/>
                  Email: <a href="mailto:dpo@calibza.com">dpo@calibza.com</a><br/>
                  Postal: CalibZa, Inc., Attn: DPO, 580 California St, 12th Floor, San Francisco, CA 94104, USA.<br/>
                  EU representative (per GDPR Art. 27): VeraSafe Ireland Ltd, Unit 3D North Point House, North Point Business Park, New Mallow Road, Cork T23 AT2P, Ireland.
                </p>
                <p>
                  We respond to verified privacy requests within 30 days. For urgent
                  security disclosures, please use <a href="mailto:security@calibza.com">security@calibza.com</a>
                  or our PGP key published at <code>calibza.com/.well-known/security.txt</code>.
                </p>
              </section>

              <div className="cz-legal-footer">
                <span>End of document · <strong>Privacy Policy v 3.2</strong></span>
                <span>Set in Fraunces · <strong>© CalibZa, Inc.</strong></span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </article>
  );
}
